import { ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';

interface PageHeaderProps {
  title: string;
  description?: string;
  children?: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'compact';
}

/**
 * ページヘッダーコンポーネント
 * 全ページで統一されたヘッダーレイアウトを提供し、タイトル、説明文、アクションボタンの配置を管理する
 * 
 * @param title - ページタイトル
 * @param description - ページ説明文
 * @param children - ヘッダー内のアクション要素
 * @param className - 追加のCSSクラス名
 * @param size - ヘッダーサイズ
 * @param variant - ヘッダーバリアント
 * @returns ページヘッダーコンポーネント
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  children,
  className,
  size = 'md',
  variant = 'default'
}) => {
  const titleSizeClasses = {
    sm: 'text-fluid-xl font-semibold',
    md: 'text-fluid-2xl font-bold',
    lg: 'text-fluid-3xl font-bold'
  };

  const spacingClasses = {
    default: 'mb-fluid-md',
    compact: 'mb-fluid-sm'
  };

  return (
    <div className={cn(spacingClasses[variant], className)}>
      <div className="flex flex-col gap-touch-gap-sm sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex-1 min-w-0">
          <h1 className={cn(
            'text-gray-900 dark:text-gray-100 tracking-tight leading-tight',
            titleSizeClasses[size]
          )}>
            {title}
          </h1>
          {description && (
            <p className="mt-1.5 text-fluid-sm text-gray-600 dark:text-gray-400 leading-relaxed max-w-3xl">
              {description}
            </p>
          )}
        </div>
        {children && (
          <div className="flex-shrink-0 mt-3 sm:mt-0">
            <div className="flex flex-col sm:flex-row gap-touch-gap-sm sm:gap-3">
              {children}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};